import { defineStore } from 'pinia'
import { computed, reactive, ref, watch } from 'vue'
import { parseQueryToFilterState, buildQueryObjectFromFilterState } from 'Modules/utils/filterUrlUtilities'
import { useRouter } from 'vue-router'
import { FilterState, getDefaultFilterState } from 'Modules/types/filterTypes'

export const useFilterStore = defineStore('filtersStore', () => {
  const router = useRouter()
  const filterState = reactive<FilterState>(getDefaultFilterState())
  const queryParamObj = ref<Record<string, string | string[]>>()
  const isQueryParamsObjDefined = computed<boolean>(() => queryParamObj.value !== undefined)
  const isUpdatingFromWatcher = ref(false)
  const isQueryInSync = ref(false)
  const areFiltersReady = computed<boolean>(
    () =>
      isUpdatingFromWatcher.value === false && isQueryInSync.value === true && isQueryParamsObjDefined.value === true
  )

  const filtersModel: { [K in keyof FilterState]: any } = {
    subjectModel: createFilterModel('subjectModel'),
    aircraft: createFilterModel('aircraft'),
    type: createFilterModel('type'),
    severity: createFilterModel('severity'),
    inspectionDefinition: createFilterModel('inspectionDefinition'),
    inspectionInstance: createFilterModel('inspectionInstance'),
    workcardDefinition: createFilterModel('workcardDefinition'),
    workcardInstance: createFilterModel('workcardInstance'),
    inspectionImage: createFilterModel('inspectionImage'),
    image: createFilterModel('image'),
    sizes: createFilterModel('sizes'),
  }

  const appliedFilterCount = computed(() => {
    return Object.keys(filterState).reduce((count, key) => {
      const value = filterState[key]

      // Count non-empty arrays
      if (Array.isArray(value) && value.length > 0) {
        const defaultValue = getDefaultFilterState()[key as keyof FilterState]
        if (Array.isArray(defaultValue) && defaultValue.length > 0 && value.length === defaultValue.length) {
          // If the selected value has the same length as a non-empty default, it's considered unapplied
          return count
        }
        return count + 1
      }

      // Special handling for "sizes" key
      if (key === 'sizes') {
        const sizes = filterState.sizes
        const hasSizeValueDefined = ['area', 'length'].some(
          (subKey) => sizes[subKey].min !== null || sizes[subKey].max !== null
        )

        if (hasSizeValueDefined) {
          return count + 1
        }
      }

      return count
    }, 0)
  })

  // Helper function to create a computed property for any key
  function createFilterModel<K extends keyof FilterState>(key: K) {
    return computed({
      get: () => filterState[key],
      set: (newValue: FilterState[K]) => {
        isUpdatingFromWatcher.value = true
        updateFilterState(key, newValue)

        // To prevent an invalid filter state, we clear the selected work card.
        if (key === 'inspectionInstance') {
          filterState.workcardDefinition = []
        }

        const routeParamObj = buildQueryObjectFromFilterState(filterState)
        queryParamObj.value = Object.fromEntries(Object.entries(routeParamObj).filter(([key]) => key !== 'image'))
        router.replace({ query: routeParamObj }).finally(() => {
          isUpdatingFromWatcher.value = false
          isQueryInSync.value = true
        })
      },
    })
  }

  function updateFilterState(path: string | string[], value: any): void {
    // Convert the path to an array if it's a string
    const keys = Array.isArray(path) ? path : path.split('.')

    // Start from the root of filterState
    let current: any = filterState

    // Traverse the path to reach the target property
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      if (!(key in current)) {
        console.warn(`Key "${key}" does not exist in filterState.`)
        return
      }
      current = current[key]
    }

    // Set the value at the target property
    const finalKey = keys[keys.length - 1]
    if (!(finalKey in current)) {
      console.warn(`Key "${finalKey}" does not exist in filterState.`)
      return
    }
    current[finalKey] = value
  }

  function invalidateFilterState() {
    isQueryInSync.value = false
  }

  watch(
    () => router.currentRoute.value.query,
    (newQuery) => {
      if (!isUpdatingFromWatcher.value) {
        isUpdatingFromWatcher.value = true
        parseQueryToFilterState(newQuery, filterState)
        queryParamObj.value = buildQueryObjectFromFilterState(filterState)
        isUpdatingFromWatcher.value = false
        isQueryInSync.value = true
      }
    }
  )

  return {
    filterState,
    filtersModel,
    appliedFilterCount,
    queryParamObj,
    isQueryParamsObjDefined,
    areFiltersReady,
    invalidateFilterState,
  }
})
